import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FaTimes, FaSave, FaSpinner, FaUpload, FaExpand, FaCompress } from 'react-icons/fa';
import Select from 'react-select/async';
import dayjs from 'dayjs';
// Icon components
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);
const API_URL = '/api';


const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false,
  className = "",
  onMultiSelectTrigger = null,
  multiSelectShortcut = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Clear search term and reset highlighted index when opening
      setSearchTerm('');
      setHighlightedIndex(-1);
      
      // Focus the search input after a short delay to ensure it's rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 0);
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    
    return options.filter(option => {
      const label = getOptionLabel(option).toLowerCase();
      const secondaryLabel = getOptionSecondaryLabel(option).toLowerCase();
      
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Check for multi-select shortcut
    if (onMultiSelectTrigger && multiSelectShortcut && e.ctrlKey && e.key === multiSelectShortcut) {
      e.preventDefault();
      onMultiSelectTrigger();
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredOptions.length - 1) {
            return prev + 1;
          } else if (filteredOptions.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredOptions.length > 0) {
            return filteredOptions.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelectOption = (option) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get selected option
  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center ${
          error ? 'border-red-400' : 'border-[#E2E8F0]'
        } ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === getOptionValue(option) ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && (
                    <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};


// ---------- Error Popup Component ----------
const ErrorPopup = ({ show, messages = [], onClose }) => {
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (!show) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => {
      try { containerRef.current?.querySelector('button')?.focus(); } catch {}
    }, 50);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [show, onClose]);

  if (!show) return null;

  const copyErrors = async () => {
    const text = messages.length ? messages.join('\n') : 'No details';
    try {
      await navigator.clipboard.writeText(text);
      // small fallback notice
      // eslint-disable-next-line no-alert
      alert('Errors copied to clipboard');
    } catch {
      // eslint-disable-next-line no-alert
      alert('Copy failed — select and copy manually.');
    }
  };

  const shortMessages = messages.filter(m => typeof m === 'string' && m.length <= 28);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} />
      <div ref={containerRef} className="relative z-10 w-11/12 max-w-lg bg-white rounded-2xl shadow-2xl p-6">
        <div className="flex items-center justify-center -mt-10 mb-3">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center shadow-sm">
            <svg className="w-8 h-8 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        </div>

        <div className="text-center mb-3">
          <h2 className="text-xl font-semibold text-red-600">Error</h2>
          <p className="text-sm text-gray-600 mt-1">
            {messages.length > 0 ? `${messages.length} problem${messages.length > 1 ? 's' : ''} found` : 'Something went wrong'}
          </p>
        </div>

        {shortMessages.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mb-3">
            {shortMessages.map((m, i) => (
              <span key={`pill-${i}`} className="text-xs px-3 py-1 bg-red-50 border border-red-100 text-red-700 rounded-full">
                {m}
              </span>
            ))}
          </div>
        )}

        

        <div className="flex justify-center items-center gap-3">
          <div>
             <button onClick={onClose} className="px-5 py-2 bg-red-600 text-white rounded-full">Try Again</button>
          </div>
          <div>
           
          </div>
        </div>
      </div>
    </div>
  );
};

const initialFields = {
  locationCode: '', locationName: '', locationTypeID: '', parentLocationID: '',
  country: '', state: '', city: '', postalCode: '', addressLine1: '', addressLine2: '',
  status: 1, BranchCode: '', department_id: '' // Added department_id field
};

const LocationFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading }) => {
  const [formData, setFormData] = useState(initialFields);
  const [errors, setErrors] = useState({});
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [locationTypes, setLocationTypes] = useState([]);
  const [parentLocations, setParentLocations] = useState([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileType, setFileType] = useState('');
  const [filePreview, setFilePreview] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Error popup state
const [errorPopup, setErrorPopup] = useState({ show: false, messages: [] });

  const [formLoading, setFormLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserValid, setIsUserValid] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false); // New state for maximize/minimize
  
  // Add branches state
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  
  // Department-related state
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  
  const tabs = [
    { id: 'details', name: 'Location Details' },
    { id: 'address', name: 'Address Information' },
    { id: 'documents', name: 'Documents' },
  ];

  let countryLibPromise = null;

const loadCountryLib = async () => {
  if (!countryLibPromise) {
    countryLibPromise = import('country-state-city');
  }
  return countryLibPromise;
};
  const loadCountries = async (inputValue) => {
  const { Country } = await loadCountryLib();
  const countries = Country.getAllCountries();

  return countries
    .filter(country =>
      country.name.toLowerCase().includes(inputValue.toLowerCase()) ||
      country.isoCode.toLowerCase().includes(inputValue.toLowerCase())
    )
    .map(country => ({
      value: country.name,
      label: `${country.name} (${country.isoCode})`
    }));
};

const loadStates = async (inputValue) => {
  if (!formData.country) return [];

  const { Country, State } = await loadCountryLib();

  const country = Country.getAllCountries()
    .find(c => c.name === formData.country);
  if (!country) return [];

  const states = State.getStatesOfCountry(country.isoCode);

  return states
    .filter(state =>
      state.name.toLowerCase().includes(inputValue.toLowerCase()) ||
      state.isoCode.toLowerCase().includes(inputValue.toLowerCase())
    )
    .map(state => ({
      value: state.name,
      label: `${state.name} (${state.isoCode})`
    }));
};

const loadCities = async (inputValue) => {
  if (!formData.country || !formData.state) return [];

  const { Country, State, City } = await loadCountryLib();

  const country = Country.getAllCountries()
    .find(c => c.name === formData.country);
  if (!country) return [];

  const state = State.getStatesOfCountry(country.isoCode)
    .find(s => s.name === formData.state);
  if (!state) return [];

  const cities = City.getCitiesOfState(country.isoCode, state.isoCode);

  return cities
    .filter(city =>
      city.name.toLowerCase().includes(inputValue.toLowerCase())
    )
    .map(city => ({
      value: city.name,
      label: city.name
    }));
};

  // Function to fetch current user
  const fetchCurrentUser = useCallback(async () => {
    try {
    const token = localStorage.getItem('authToken'); // store token after login
    if (!token) {
      setIsUserValid(false);
      setAuthError('No token found. Please log in again.');
      return false;
    }
    const userRes = await fetch(`${API_URL}/current-user`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    const userData = await userRes.json();
      
      if (userData.user && userData.user.UserID) {
        console.log('Setting current user:', userData.user);
        console.log('User ID:', userData.user.UserID, 'Type:', typeof userData.user.UserID);
        setCurrentUser(userData.user);
        setIsUserValid(true);
        setAuthError(null);
        return true;
      } else {
        console.error('Invalid user data received:', userData);
        setIsUserValid(false);
        setAuthError('User information not available. Please log in again.');
        return false;
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      setIsUserValid(false);
      setAuthError('Error fetching user information. Please refresh the page.');
      return false;
    }
  }, []);
  
  // Function to fetch departments
  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const response = await fetch(`${API_URL}/departments`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      const data = await response.json();
      console.log('Fetched departments:', data);
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };
  
  // Function to fetch screen configuration
  const fetchScreenConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/screens`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      const data = await response.json();
      console.log('Fetched screen configurations:', data);
      
      // Find the location screen configuration
      const locationScreen = data.screens.find(screen => 
        screen.ScreenName.toLowerCase() === 'Warehouse'.toLowerCase()
      );
      
      console.log('Location screen configuration:', locationScreen);
      if (locationScreen) {
        setScreenConfig(locationScreen);
        // Show department field if ShowOnHeader is 1
        const shouldShowDepartment = locationScreen.ShowOnHeader === true;
        setShowDepartmentField(shouldShowDepartment);
        
        // If department field should be shown, fetch departments
        if (shouldShowDepartment) {
          fetchDepartments();
        }
      }
    } catch (error) {
      console.error('Error fetching screen configuration:', error);
      setShowDepartmentField(false);
    }
  };
  
  // Function to fetch branches
  const fetchBranches = useCallback(async () => {
    setLoadingBranches(true);
    try {
      const response = await fetch(`${API_URL}/branches`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }}); // Replace with your actual branches API endpoint
      const data = await response.json();
      setBranches(data);
    } catch (error) {
      console.error('Error fetching branches:', error);
      // If API fails, set some default branches
    } finally {
      setLoadingBranches(false);
    }
  }, []);
  
  const fetchLocationData = useCallback(async () => {
    try {
      setLoadingDetails(true);
      const response = await fetch(`${API_URL}/locations/${initialData}`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (response.ok) {
        const location = await response.json();
        console.log('Location data received:', {
          locationID: location.LocationID,
            BranchCode: location.BranchCode,
          status: location.Status,
          statusType: typeof location.Status,
          createdBy: location.CreatedBy,
          updatedBy: location.UpdatedBy
        });

         const departmentId = location.DepartmentID || location.department_id || '';
      console.log('Setting department_id to:', departmentId, typeof departmentId);
        
        // Keep the CreatedBy and UpdatedBy information for debugging
        setFormData({
          locationCode: location.LocationCode,
          locationName: location.LocationName,
          locationTypeID: location.LocationTypeID,
          parentLocationID: location.ParentLocationID || '',
          country: location.Country,
          state: location.State,
          city: location.City,
          postalCode: location.PostalCode,
          addressLine1: location.AddressLine1,
          addressLine2: location.AddressLine2 || '',
          status: location.Status ? 1 : 0, // Convert boolean to number
          BranchCode: location.BranchCode || '', // Added BranchCode
          department_id: departmentId.toString(), // Added department_id
          // Store the user IDs for debugging
          CreatedBy: location.CreatedBy,
          UpdatedBy: location.UpdatedBy
        });
        setFileName(location.FileName || '');
        setFileType(location.FileType || '');
        setFilePreview({});
      }
    } catch (error) {
      console.error('Error fetching location data:', error);
    } finally {
      setLoadingDetails(false);
    }
  }, [initialData]);
  
  // Reset form state on close
  useEffect(() => {
    if (!open) {
      setFormData(initialFields);
      setErrors({});
      setSelectedFile(null);
      setFileName('');
      setFileType('');
      setFilePreview({});
      setLoadingDetails(false);
      setActiveTab('details');
      setCurrentStep(0);
      setIsMaximized(false); // Reset maximized state when closing
      setShowDepartmentField(false); // Reset department field state
      setScreenConfig(null); // Reset screen config
    }
  }, [open]);
  
  // Fetch data on open
  useEffect(() => {
    if (open) {
      // Fetch current user when modal opens
      fetchCurrentUser().then(() => {
        fetchLocationTypes();
        fetchParentLocations();
        fetchBranches(); // Added fetchBranches
        fetchLocationData();
        fetchScreenConfig(); // Fetch screen configuration to determine if department field should be shown
        if (!isEdit) {
          fetchNextLocationCode();
        }
        setCurrentStep(0);
        setActiveTab('details');
      });
    }
  }, [open, isEdit, fetchCurrentUser, fetchBranches,fetchLocationData]);
  
  useEffect(() => {
    if (isEdit && initialData) {
      fetchLocationData();
    }
  }, [isEdit, initialData, fetchLocationData]);
  
  // Fetch current user on mount
  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);
  
  const fetchLocationTypes = async () => {
    try {
      setIsLoadingTypes(true);
      const response = await fetch(`${API_URL}/location-types`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (response.ok) {
        const data = await response.json();
        setLocationTypes(data.locationTypes || []);
      }
    } catch (error) {
      console.error('Error fetching location types:', error);
    } finally {
      setIsLoadingTypes(false);
    }
  };
  
  const fetchParentLocations = async () => {
    try {
      setIsLoadingLocations(true);
      const response = await fetch(`${API_URL}/locations`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (response.ok) {
        const data = await response.json();
        setParentLocations(data || []);
      }
    } catch (error) {
      console.error('Error fetching parent locations:', error);
    } finally {
      setIsLoadingLocations(false);
    }
  };
  
  const fetchNextLocationCode = async () => {
    try {
      setIsLoadingCode(true);
      const response = await fetch(`${API_URL}/locationlatestcode`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, locationCode: data.nextCode }));
      }
    } catch (error) {
      console.error('Error fetching next location code:', error);
      setFormData(prev => ({ ...prev, locationCode: 'LOC-01' }));
    } finally {
      setIsLoadingCode(false);
    }
  };
  

  
  const handleInputChange = (field, value) => {
    // Convert text input values to uppercase
    if (typeof value === 'string' && field !== 'country' && field !== 'state' && field !== 'city') {
      value = value;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    // Reset dependent fields when country or state changes
    if (field === 'country') {
      setFormData(prev => ({ ...prev, state: '', city: '' }));
    } else if (field === 'state') {
      setFormData(prev => ({ ...prev, city: '' }));
    }
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
      setFileType(file.type);
      setFilePreview(prev => ({
        ...prev,
        attachment: URL.createObjectURL(file)
      }));
    }
  };
  
  const handleFileRemove = () => {
    setSelectedFile(null);
    setFileName('');
    setFileType('');
    setFilePreview(prev => {
      const newPreviews = { ...prev };
      delete newPreviews.attachment;
      return newPreviews;
    });
  };
  
  const validate = () => {
  const errs = {};
  if (!formData.locationName) errs.locationName = 'Required';
  if (!formData.locationTypeID) errs.locationTypeID = 'Required';
  if (!formData.BranchCode) errs.BranchCode = 'Required'; // Added branch validation
  // Add department validation only if the field is shown
  if (showDepartmentField && !formData.department_id) errs.department_id = 'Required';
  if (!formData.country) errs.country = 'Required';
  if (!formData.state) errs.state = 'Required';
  if (!formData.city) errs.city = 'Required';
  if (!formData.postalCode) errs.postalCode = 'Required';
  if (!formData.addressLine1) errs.addressLine1 = 'Required';

  setErrors(errs);

  const isValid = Object.keys(errs).length === 0;
  if (!isValid) {
    const messages = [];
    Object.entries(errs).forEach(([k, v]) => {
      const pretty = k === 'locationName' ? 'Location Name' :
                     k === 'locationTypeID' ? 'Location Type' :
                     k === 'BranchCode' ? 'Branch' :
                     k === 'addressLine1' ? 'Address Line 1' :
                     k === 'postalCode' ? 'Postal/Zip Code' :
                     k === 'department_id' ? 'Department' :
                     (k.charAt(0).toUpperCase() + k.slice(1));
      messages.push(`${pretty}: ${v}`);
    });
    setErrorPopup({ show: true, messages });
  }

  return isValid;
};

  
  const goToNextStep = () => {
    if (currentStep < tabs.length - 1) {
      setCurrentStep(prev => prev + 1);
      setActiveTab(tabs[currentStep + 1].id);
    }
  };
  
  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setActiveTab(tabs[currentStep - 1].id);
    }
  };
  
  const saveCurrentStep = async () => {
    setFormLoading(true);
    
    try {
      // Simulate saving - in a real app, this would be an API call
      console.log(`Saving step ${currentStep + 1}: ${tabs[currentStep].name}`);
      
      // If this is the last step, submit the entire form
      if (currentStep === tabs.length - 1) {
        await handleSubmit();
      } else {
        // Move to next step
        goToNextStep();
      }
    } catch (error) {
      console.error('Error saving step:', error);
 setErrorPopup({ show: true, messages: ['An error occurred while saving. Please try again.'] });

    } finally {
      setFormLoading(false);
    }
  };
  // Format server error payload into a messages array
const normalizeServerError = (errPayload) => {
  const msgs = [];
  if (!errPayload) return msgs;
  if (typeof errPayload === 'string') return [errPayload];
  if (errPayload.message) msgs.push(errPayload.message);
  if (errPayload.errors && typeof errPayload.errors === 'object') {
    Object.values(errPayload.errors).forEach(v => {
      if (Array.isArray(v)) v.forEach(s => msgs.push(s));
      else msgs.push(v);
    });
  }
  return msgs.length ? msgs : ['An error occurred'];
};

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (isSubmitting || formLoading || loading) {
      console.log('Form submission already in progress, ignoring duplicate submission');
      return;
    }
    
    if (!validate()) return;
    
    // Check if we have a valid current user
    if (!isUserValid || !currentUser || !currentUser.UserID) {
      setErrorPopup({ show: true, messages: [authError || 'User information not available. Please refresh the page and log in again.'] });

      return;
    }
    
    setIsSubmitting(true);
    setFormLoading(true);
    
    const submitData = new FormData();
    // Add all form fields with proper field names matching backend expectations
    submitData.append('locationCode', formData.locationCode);
    submitData.append('locationName', formData.locationName);
    submitData.append('locationTypeID', formData.locationTypeID);
    submitData.append('parentLocationID', formData.parentLocationID || '');
    submitData.append('country', formData.country || '');
    submitData.append('state', formData.state || '');
    submitData.append('city', formData.city || '');
    submitData.append('postalCode', formData.postalCode || '');
    submitData.append('addressLine1', formData.addressLine1 || '');
    submitData.append('addressLine2', formData.addressLine2 || '');
    submitData.append('status', formData.status ? '1' : '0'); // Convert to string for FormData
    submitData.append('BranchCode', formData.BranchCode || ''); // Added BranchCode
    
    
    // Add department_id if showDepartmentField is true
    if (showDepartmentField) {
      submitData.append('department_id', formData.department_id || '');
    }
    
    // Use the user ID as is, without converting to integer
    const userId = currentUser.UserID;
    console.log('Using user ID (raw):', userId, 'Type:', typeof userId);
    
    if (isEdit) {
      submitData.append('updatedBy', userId);
      console.log('Appended updatedBy:', userId);
    } else {
      submitData.append('createdBy', userId);
      console.log('Appended createdBy:', userId);
    }
    
    // Add file if selected
    if (selectedFile) {
      submitData.append('file', selectedFile);
      submitData.append('fileName', fileName);
      submitData.append('fileType', fileType);
    }
    
    // Log FormData contents for debugging
    console.log('FormData contents:');
    for (let pair of submitData.entries()) {
      console.log(`${pair[0]}: ${pair[1]} (type: ${typeof pair[1]})`);
    }
    
    try {
  // Attempt submit via parent handler
  await onSubmit(submitData);
  onClose();
} catch (error) {
  console.error('Error saving location:', error);

  // Try to extract server error details
  let msgs = [];
  try {
    if (error && typeof error.json === 'function') {
      const parsed = await error.json();
      msgs = normalizeServerError(parsed);
    } else if (error && typeof error === 'object') {
      msgs = normalizeServerError(error);
    } else {
      msgs = [String(error) || 'An error occurred while saving the location'];
    }
  } catch (parseErr) {
    msgs = [error.message || 'An error occurred while saving the location'];
  }

  // Set any field-level errors returned by backend
  if (error && error.errors && typeof error.errors === 'object') {
    setErrors(prev => ({ ...prev, ...error.errors }));
  }

  setErrorPopup({ show: true, messages: msgs });
} finally {
  setFormLoading(false);
  setIsSubmitting(false);
}

  };
  
  // Toggle maximize/minimize function
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  if (!open) return null;
  
  const renderFormContent = () => (
  <>
    {/* Fixed Header */}
    <div className="flex-shrink-0 p-8 pb-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-[#384959]">{isEdit ? 'Edit Location' : 'Create New Location'}</h2>
        <div className="flex items-center gap-3">
          <button 
            onClick={toggleMaximize}
            className="text-[#A0AEC0] hover:text-[#384959] transition-colors"
            title={isMaximized ? "Minimize" : "Maximize"}
          >
            {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
          </button>
          <button onClick={onClose} className="text-[#A0AEC0] hover:text-[#384959] transition-colors">
            <CloseIcon size={24} />
          </button>
        </div>
      </div>
    </div>
    
    {/* User validation warning */}
    {open && !isUserValid && (
      <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">
              {authError || 'User information not available. Please refresh the page and log in again.'}
            </p>
          </div>
        </div>
      </div>
    )}
    
    {/* Scrollable Content Area with all tabs */}
    <div className="flex-grow overflow-y-auto custom-scrollbar px-8">
      <form onSubmit={handleSubmit} className="space-y-6 pb-6">
        
        {/* Location Details Section */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
          <div className="border-b border-[#E2E8F0] px-6 py-4 bg-[#F9FAFB] rounded-t-xl">
            <div className="flex items-center">
              <h3 className="ml-3 text-lg font-semibold text-[#384959]">Location Details</h3>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 gap-4">
              <div className="flex flex-col gap-1">
                <label className="block text-sm font-medium text-[#64748b]">Location Code</label>
                <input
                  type="text"
                  value={isLoadingCode ? 'Loading...' : formData.locationCode}
                  readOnly
                  className="w-full rounded-md border border-[#E2E8F0] bg-[#F1F5F9] text-gray-500 px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] uppercase-input"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="block text-sm font-medium text-[#64748b]">Location Name *</label>
                <input
                  type="text"
                  name="locationName"
                  autoComplete="off"
                  value={formData.locationName}
                  onChange={(e) => handleInputChange('locationName', e.target.value)}
                  className={`w-full rounded-md border ${errors.locationName ? 'border-red-400' : 'border-[#E2E8F0]'} bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors uppercase-input`}
                />
                {errors.locationName && <span className="text-xs text-red-500 mt-1">{errors.locationName}</span>}
              </div>
              
              {/* Added Branch Selection Field */}
              <div className="flex flex-col gap-1">
                <label className="block text-sm font-medium text-[#64748b]">Branch *</label>
                <SearchableDropdown
    options={branches}
    value={formData.BranchCode}
    onChange={(value) => handleInputChange('BranchCode', value)}
    getOptionLabel={(branch) => `${branch.BranchCode} - ${branch.BranchName}`}
    getOptionValue={(branch) => branch.BranchCode}
    getOptionSecondaryLabel={(branch) => branch.BranchName}
    placeholder={loadingBranches ? 'Loading branches...' : 'Select Branch'}
    error={errors.BranchCode}
    disabled={loadingBranches}
    className="uppercase"
  />
                {errors.BranchCode && <span className="text-xs text-red-500 mt-1">{errors.BranchCode}</span>}
              </div>
              
              {/* Department Selection Field - Only shown if ShowOnHeader is 1 */}
              {showDepartmentField && (
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-[#64748b]">Department *</label>
                  <SearchableDropdown
      options={departments}
      value={formData.department_id}
      onChange={(value) => handleInputChange('department_id', value)}
      getOptionLabel={(dept) => `${dept.DepartmentCode} - ${dept.DepartmentName}`}
      getOptionValue={(dept) => dept.DepartmentID.toString()}
      getOptionSecondaryLabel={(dept) => dept.DepartmentName}
      placeholder={loadingDepartments ? 'Loading departments...' : 'Select Department'}
      error={errors.department_id}
      disabled={loadingDepartments}
      className="uppercase"
    />
                  {errors.department_id && <span className="text-xs text-red-500 mt-1">{errors.department_id}</span>}
                </div>
              )}
              
              <div className="flex flex-col gap-1">
                <label className="block text-sm font-medium text-[#64748b]">Location Type *</label>
                <select
                  name="locationTypeID"
                  value={formData.locationTypeID}
                  onChange={(e) => handleInputChange('locationTypeID', e.target.value)}
                  className={`w-full rounded-md border ${errors.locationTypeID ? 'border-red-400' : 'border-[#E2E8F0]'} bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors`}
                  disabled={isLoadingTypes}
                >
                  <option value="">Select Location Type</option>
                  {locationTypes.map(type => (
                    <option key={type.LocationTypeID} value={type.LocationTypeID}>
                      {type.LocationTypeName}
                    </option>
                  ))}
                </select>
                {errors.locationTypeID && <span className="text-xs text-red-500 mt-1">{errors.locationTypeID}</span>}
              </div>
              <div className="flex flex-col gap-1">
                <label className="block text-sm font-medium text-[#64748b]">Parent Location</label>
                 <SearchableDropdown
    options={parentLocations}
    value={formData.parentLocationID}
    onChange={(value) => handleInputChange('parentLocationID', value)}
    getOptionLabel={(location) => `${location.LocationName} (${location.LocationCode})`}
    getOptionValue={(location) => location.LocationID}
    getOptionSecondaryLabel={(location) => location.LocationName}
    placeholder={isLoadingLocations ? 'Loading parent locations...' : 'Select Parent Location'}
    disabled={isLoadingLocations}
    className="uppercase"
  />
              </div>
              <div className="flex flex-col gap-1">
                <label className="block text-sm font-medium text-[#64748b]">Status *</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', parseInt(e.target.value))}
                  className="w-full rounded-md border border-[#E2E8F0] bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        
        {/* Address Information Section */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
          <div className="border-b border-[#E2E8F0] px-6 py-4 bg-[#F9FAFB] rounded-t-xl">
            <div className="flex items-center">
              <h3 className="ml-3 text-lg font-semibold text-[#384959]">Address Information</h3>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1 md:col-span-2">
                <label className="block text-sm font-medium text-[#64748b]">Address Line 1 *</label>
                <input
                  type="text"
                  name="addressLine1"
                  autoComplete="off"
                  value={formData.addressLine1}
                  onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                  className={`w-full rounded-md border ${errors.addressLine1 ? 'border-red-400' : 'border-[#E2E8F0]'} bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors uppercase-input`}
                />
                {errors.addressLine1 && <span className="text-xs text-red-500 mt-1">{errors.addressLine1}</span>}
              </div>
              <div className="flex flex-col gap-1 md:col-span-2">
                <label className="block text-sm font-medium text-[#64748b]">Address Line 2</label>
                <input
                  type="text"
                  name="addressLine2"
                  autoComplete="off"
                  value={formData.addressLine2}
                  onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                  className="w-full rounded-md border border-[#E2E8F0] bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors uppercase-input"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="block text-sm font-medium text-[#64748b]">Country *</label>
                <Select
                  loadOptions={loadCountries}
                  defaultOptions
                  value={formData.country ? { value: formData.country, label: formData.country } : null}
                  onChange={(option) => handleInputChange('country', option?.value || '')}
                  classNamePrefix="react-select"
                  placeholder="Search country..."
                  isClearable
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderRadius: '0.375rem',
                      borderColor: errors.country ? 'rgb(248 113 113)' : 'rgb(226, 232, 240)',
                      backgroundColor: 'white',
                      boxShadow: state.isFocused ? '0 0 0 1px rgb(106, 137, 167)' : 'none',
                      '&:hover': {
                        borderColor: state.isFocused ? 'rgb(106, 137, 167)' : 'rgb(226, 232, 240)',
                      },
                    }),
                    input: (baseStyles) => ({
                      ...baseStyles,
                      'input': {
                        boxShadow: 'none !important',
                      },
                    }),
                  }}
                  noOptionsMessage={() => "No countries found."}
                />
                {errors.country && <span className="text-xs text-red-500 mt-1">{errors.country}</span>}
              </div>
              <div className="flex flex-col gap-1">
                <label className="block text-sm font-medium text-[#64748b]">State/Province *</label>
                <Select
                  loadOptions={loadStates}
defaultOptions
                  value={formData.state ? { value: formData.state, label: formData.state } : null}
                  onChange={(option) => handleInputChange('state', option?.value || '')}
                  classNamePrefix="react-select"
                  placeholder={formData.country ? "Search state..." : "Select a country first"}
                  isClearable
                  isDisabled={!formData.country}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderRadius: '0.375rem',
                      borderColor: errors.state ? 'rgb(248 113 113)' : 'rgb(226, 232, 240)',
                      backgroundColor: state.isDisabled ? '#f1f5f9' : 'white',
                      boxShadow: state.isFocused ? '0 0 0 1px rgb(106, 137, 167)' : 'none',
                      '&:hover': {
                        borderColor: state.isFocused ? 'rgb(106, 137, 167)' : 'rgb(226, 232, 240)',
                      },
                    }),
                    input: (baseStyles) => ({
                      ...baseStyles,
                      'input': {
                        boxShadow: 'none !important',
                      },
                    }),
                  }}
                  noOptionsMessage={() => "No states found."}
                />
                {errors.state && <span className="text-xs text-red-500 mt-1">{errors.state}</span>}
              </div>
              <div className="flex flex-col gap-1">
                <label className="block text-sm font-medium text-[#64748b]">City *</label>
                <Select
                  loadOptions={loadCities}
defaultOptions
                  value={formData.city ? { value: formData.city, label: formData.city } : null}
                  onChange={(option) => handleInputChange('city', option?.value || '')}
                  classNamePrefix="react-select"
                  placeholder={formData.country && formData.state ? "Search city..." : "Select a state first"}
                  isClearable
                  isDisabled={!formData.country || !formData.state}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderRadius: '0.375rem',
                      borderColor: errors.city ? 'rgb(248 113 113)' : 'rgb(226, 232, 240)',
                      backgroundColor: state.isDisabled ? '#f1f5f9' : 'white',
                      boxShadow: state.isFocused ? '0 0 0 1px rgb(106, 137, 167)' : 'none',
                      '&:hover': {
                        borderColor: state.isFocused ? 'rgb(106, 137, 167)' : 'rgb(226, 232, 240)',
                      },
                    }),
                    input: (baseStyles) => ({
                      ...baseStyles,
                      'input': {
                        boxShadow: 'none !important',
                      },
                    }),
                  }}
                  noOptionsMessage={() => "No cities found."}
                />
                {errors.city && <span className="text-xs text-red-500 mt-1">{errors.city}</span>}
              </div>
              <div className="flex flex-col gap-1">
                <label className="block text-sm font-medium text-[#64748b]">Postal/Zip Code *</label>
                <input
                  type="text"
                  name="postalCode"
                  autoComplete="off"
                  value={formData.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  className={`w-full rounded-md border ${errors.postalCode ? 'border-red-400' : 'border-[#E2E8F0]'} bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors uppercase-input`}
                />
                {errors.postalCode && <span className="text-xs text-red-500 mt-1">{errors.postalCode}</span>}
              </div>
            </div>
          </div>
        </div>
        
        {/* Documents Section */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
          <div className="border-b border-[#E2E8F0] px-6 py-4 bg-[#F9FAFB] rounded-t-xl">
            <div className="flex items-center">
              <h3 className="ml-3 text-lg font-semibold text-[#384959]">Documents</h3>
            </div>
          </div>
          <div className="p-6">
            <div className="flex flex-col gap-2">
              <label className="block text-sm font-medium text-[#64748b]">Address Proof Document (Optional)</label>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="hidden"
                  id="attachment-upload"
                />
                <label
                  htmlFor="attachment-upload"
                  className="flex items-center gap-2 px-4 py-2 bg-[#6A89A7] text-white rounded-md cursor-pointer hover:bg-[#88BDF2] transition-colors duration-200"
                >
                  <FaUpload /> Upload Document
                </label>
                {fileName && (
                  <span className="text-sm text-[#64748b]">Selected: {fileName}</span>
                )}
              </div>
              {filePreview.attachment && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={filePreview.attachment} alt="Document preview" className="w-20 h-20 object-cover rounded" />
                  <button
                    type="button"
                    onClick={handleFileRemove}
                    className="p-1 text-red-500 hover:text-red-700 transition-colors"
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
    
    {/* Fixed Footer with Buttons */}
    <div className="flex-shrink-0 p-8 pt-0">
      <div className="flex justify-between gap-3">
        <button 
          type="button" 
          className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ease-in-out
            ${currentStep === 0 
              ? 'text-[#CBD5E1] cursor-not-allowed' 
              : 'text-[#64748b] hover:bg-[#F1F5F9]'}`}
          onClick={goToPreviousStep}
          disabled={currentStep === 0}
        >
          Previous
        </button>
        <div className="flex gap-3">
          <button
            type="button"
            className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={saveCurrentStep}
            className="px-6 py-3 rounded-lg text-white font-semibold bg-[#6A89A7] hover:bg-[#88BDF2] shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            disabled={loading || isLoadingCode || formLoading || isSubmitting || !isUserValid}
          >
            {formLoading || isSubmitting || loading ? (
              <>
                <FaSpinner className="animate-spin mr-2" /> Saving...
              </>
            ) : (
              currentStep === tabs.length - 1 ? (isEdit ? 'Update ' : 'Create ') : 'Continue'
            )}
          </button>
        </div>
      </div>
    </div>
  </>
);
  
  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out
        ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
    >
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          /* Style for uppercase text inputs */
          .uppercase-input {
            text-transform: uppercase;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      {/* This is the actual slide-in panel with fixed header and scrollable content */}
      <div
        className={`bg-white shadow-2xl ${isMaximized ? 'w-full h-full' : 'w-full max-w-6xl h-full'} flex flex-col
          transition-transform transform-gpu duration-300 ease-in-out
          ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {loadingDetails || isLoadingCode ? (
          <div className="flex-grow flex items-center justify-center">
            <div className="flex items-center gap-4 text-[#384959]">
              <FaSpinner className="animate-spin text-xl" />
              <span>Loading location details...</span>
            </div>
          </div>
        ) : (
          renderFormContent()
        )}
      </div>
      {/* Error popup */}
<ErrorPopup
  show={errorPopup.show}
  messages={errorPopup.messages}
  onClose={() => setErrorPopup({ show: false, messages: [] })}
/>

    </div>
  );
};
export default LocationFormModal;